import {type Moment} from 'moment';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';

import {Button, EMPHASIS, FlexRow, IconTooltip, SegmentedControl, Tabs} from '@forecast-it/design-system';

import HeaderLabel from '../../../../components/new-ui/HeaderLabel';
import {APPROVAL_STATUS, ApprovalStatus, GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR} from '../../../../constants';
import {EVENT_ID} from '../../../../containers/event_manager';
import useSubscription from '../../../../forecast-app/shared/hooks/useSubscription';
import ForecastQueryRenderer from '../../../../ForecastQueryRenderer';
import {RelayProps} from '../../../../relay-helpers/TSRelayConverter';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {trackTimeRegistrationSuggestionsShown} from '../../../../tracking/tracking_types/TrackTimeRegistrationSuggestionsShown';
import TheEye from '../../../shared/components/headers/header-bar/TheEye';
import InlineLoader from '../../../shared/components/inline-loader/inline_loader';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import Util from '../../../shared/util/util';
import {TimeRegistrationTargetSuggstionsData} from '../time-registration-target-suggestions/TimeRegistrationTargetSuggestionsFetch';
import {getValidSuggestions} from '../time-registration-target-suggestions/TimeRegistrationTargetSuggestionUtil';
import {TIME_PERIOD, type TimePeriod} from '../timesheets_change_date_view';
import {getHolidaysOfTheDay} from '../timesheets_person_data';
import {TIMESHEET_THIS_WEEKS_WORK_PAGE_SIZE} from '../Constants';
import {
	BottomSectionWrapper,
	DateInfoSectionWrapper,
	InfoSectionWrapper,
	TabSectionWrapper,
	TableWrapper,
} from '../timesheets.styled';
import {LOCALSTORAGE_TIMESHEETS_SELECTED_TAB} from '../top-section/TimesheetsCalendarUtil';
import {TimesheetsFilters_viewer} from './__generated__/TimesheetsFilters_viewer.graphql';
import {addTimeRegTypes, groupRegs} from './timesheets-time-registration-table/timesheet-table-util/DataUtil';
import {TimesheetsTaskCountQueryResponse} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetsTaskCountQuery.graphql';
import {TimesheetsTaskCountQuery} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetsTaskCountQuery';
import TimesheetThisWeeksWorkTableWrapper, {
	TimesheetThisWeeksWorkTableWrapperQuery,
} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetThisWeeksWorkTableWrapper';
import TimesheetTimeTableWrapper, {
	TimesheetTimeTableWrapperQuery,
} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetTimeTableWrapper';
import {DefaultFiltersType, TimesheetsFilters} from './TimesheetsFilters';
import {suggestionsWithoutTimeRegs} from './TimesheetUtil';

import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {
	usingInternalTimeApproval as isUsingInternalTimeApproval,
	usingTimeApproval as isUsingTimeApproval,
} from '../../../shared/util/FeatureUtil';
import type {TimesheetThisWeeksWorkTableWrapper_viewer} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetThisWeeksWorkTableWrapper_viewer.graphql';
import type {TimesheetTimeTableWrapper_viewer} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetTimeTableWrapper_viewer.graphql';
import {useTimeRegistrationSettings} from '@/forecast-app/shared/hooks/useTimeRegistrationSettings';
import TimesheetInternalTimeTableWrapper, {
	TimesheetInternalTimeTableWrapperQuery,
} from './timesheets-time-registration-table/timesheet-table-wrapper/TimesheetInternalTimeTableWrapper';
import {TimesheetInternalTimeTableWrapper_viewer} from './timesheets-time-registration-table/timesheet-table-wrapper/__generated__/TimesheetInternalTimeTableWrapper_viewer.graphql';
import {SortableColumn, TimesheetColumnSort, usePersistentTimesheetColumnSort} from './usePersistentTimesheetColumnSort';
import {useStoredState} from '../../../shared/hooks/useStoredState';
import {useTimesheetColumnEyeOptions} from './timesheets-time-registration-table/useTimesheetColumnEyeOptions';

enum TimesheetTabName {
	FindTasks = 'FindTasks',
	Timesheet = 'Timesheet',
	InternalTime = 'InternalTime',
}

function isTimesheetTab(value: unknown): value is TimesheetTabName {
	return typeof value === 'string' && (Object.values(TimesheetTabName) as string[]).includes(value);
}

type TaskTypeSearchQuery = {
	filters: {
		field: keyof typeof GLOBAL_FILTER_FIELD;
		operator: keyof typeof GLOBAL_FILTER_OPERATOR;
		value: unknown[];
	}[];
};

const ColumnValueMap: Record<SortableColumn, string> = {
	[SortableColumn.ProjectName]: 'project-name',
	[SortableColumn.TaskName]: 'task-name',
	[SortableColumn.Phase]: 'phase-name',
	[SortableColumn.Client]: 'client-name',
	[SortableColumn.ProjectIndicator]: 'project-id',
	[SortableColumn.Star]: 'starred',
};
function serialiseSort(sort: TimesheetColumnSort): string {
	const valuePrefix = ColumnValueMap[sort.value];
	const directionSuffix = sort.ascending ? 'asc' : 'dsc';

	return `${valuePrefix}-${directionSuffix}`;
}

type ViewerType = {
	company: {
		id: string;
		harvestEnabled: boolean;
		unit4Enabled: boolean;
		internalTimes: {
			totalCount: number;
		};
		useTimeApproval: boolean;
		useInternalTimeApproval: boolean;
	};
	harvestUser: boolean;
	unit4User: boolean;
};

type TimesheetTabProps = {
	timeRegsForPeriod: unknown;
};
const TimesheetTab = ({timeRegsForPeriod}: TimesheetTabProps) => {
	const {formatMessage} = useIntl();

	// Group filtered timereg by type to show the row-count to make the number comparable to the "this weeks work" count.
	const groupedTimeRegs = groupRegs(addTimeRegTypes(timeRegsForPeriod));
	const timesheetItemsCount = groupedTimeRegs.size;

	return (
		<Tabs.Tab data-cy={'timesheet-tab'} value={TimesheetTabName.Timesheet}>
			<FormattedMessage id="timesheets.tabs.timesheets.title" values={{count: timesheetItemsCount}} />
			<IconTooltip>
				<IconTooltip.Item
					title={formatMessage({id: 'timesheets.tabs.timesheets.tooltip_title'})}
					details={formatMessage({id: 'timesheets.tabs.timesheet.tooltip'})}
				/>
			</IconTooltip>
		</Tabs.Tab>
	);
};

type FindTasksTabProps = {
	taskTypeSearchQuery: TaskTypeSearchQuery;
};
const FindTasksTab = ({taskTypeSearchQuery}: FindTasksTabProps) => {
	const {formatMessage} = useIntl();

	type TimesheetsTaskCountVariables = {taskTypeSearchQuery: typeof taskTypeSearchQuery};
	const {fetch, data} = useForecastFetchQuery<TimesheetsTaskCountQueryResponse, TimesheetsTaskCountVariables>(
		TimesheetsTaskCountQuery
	);
	const timeSheetsTaskCountData: TimesheetsTaskCountQueryResponse | null = data;
	useEffect(() => {
		if (taskTypeSearchQuery?.filters) {
			fetch({
				taskTypeSearchQuery: taskTypeSearchQuery,
			});
		}
	}, [taskTypeSearchQuery]);
	const thisWeeksWorkItemsCount = timeSheetsTaskCountData?.viewer?.company?.allTasksCount || 0;

	return (
		<Tabs.Tab data-cy={'this-weeks-work-tab'} value={TimesheetTabName.FindTasks}>
			<FormattedMessage id="timesheets.tabs.tasks.title" values={{count: thisWeeksWorkItemsCount}} />
			<IconTooltip>
				<IconTooltip.Item
					title={formatMessage({id: 'timesheets.tabs.tasks.tooltip_title'})}
					details={formatMessage({id: 'timesheets.tabs.task.tooltip'})}
				/>
			</IconTooltip>
		</Tabs.Tab>
	);
};

type InternalTimeTabProps = {
	viewer: ViewerType;
};

const InternalTimeTab = ({viewer}: InternalTimeTabProps) => {
	const {formatMessage} = useIntl();

	const {allowInternalTime} = useTimeRegistrationSettings();

	const internalTimesCount = viewer.company.internalTimes.totalCount;

	const showInternalTimeTab = allowInternalTime && internalTimesCount > 0;
	return showInternalTimeTab ? (
		<Tabs.Tab data-cy={'internal-time-tab'} value={TimesheetTabName.InternalTime}>
			<FormattedMessage id="timesheets.tabs.internal_time.title" />
			<IconTooltip>
				<IconTooltip.Item
					title={formatMessage({id: 'timesheets.tabs.internal_time.tooltip_title'})}
					details={formatMessage({id: 'timesheets.tabs.internal_time.tooltip'})}
				/>
			</IconTooltip>
		</Tabs.Tab>
	) : null;
};

type DateInfoSectionProps = {
	selectedTab: TimesheetTabName;
	currentViewingDate: Moment;
	weekStart: Moment;
	weekEnd: Moment;
	showAllTimeTasks: boolean;
	timePeriod: TimePeriod;
	holidays: {node: {name: string}}[];
};

const DateInfoSection = ({
	selectedTab,
	currentViewingDate,
	weekStart,
	weekEnd,
	showAllTimeTasks,
	timePeriod,
	holidays,
}: DateInfoSectionProps) => {
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const period =
		selectedTab === TimesheetTabName.FindTasks && showAllTimeTasks
			? 'All Time'
			: isWeekView
			? `${weekStart.format('D')} - ${weekEnd.format('D MMMM')}`
			: currentViewingDate.format('dddd D MMMM');

	const infoText =
		selectedTab === TimesheetTabName.Timesheet ? (
			<FormattedMessage id={'timesheets.tabs.common.info_text'} values={{entity: 'time registrations', period}} />
		) : selectedTab === TimesheetTabName.FindTasks ? (
			<FormattedMessage id={'timesheets.tabs.common.info_text'} values={{entity: 'tasks', period}} />
		) : null;

	const showHolidays = selectedTab === TimesheetTabName.Timesheet && !isWeekView;

	return (
		<DateInfoSectionWrapper color="medium" size="2">
			{infoText}
			{showHolidays && holidays?.length > 0 && (
				<div className={'holiday-names'}>{`(${holidays.map(h => h.node.name).join(', ')})`}</div>
			)}
		</DateInfoSectionWrapper>
	);
};

type PeriodToggleProps = {
	timePeriod: TimePeriod;
	setTimePeriod: (period: TimePeriod) => void;
};

const PeriodToggle = ({timePeriod, setTimePeriod}: PeriodToggleProps) => {
	const {formatMessage} = useIntl();

	return (
		<SegmentedControl
			value={timePeriod}
			onValueChange={value => {
				setTimePeriod(value);
				trackEvent('Period Selector', 'Toggled', {timePeriod: value});
			}}
			data-cy={'period-toggle'}
		>
			<SegmentedControl.Item
				value={TIME_PERIOD.DAY_VIEW}
				label={formatMessage({id: 'overview_time.day'})}
				data-cy={'day'}
			>
				<FormattedMessage id="overview_time.day" />
			</SegmentedControl.Item>
			<SegmentedControl.Item
				value={TIME_PERIOD.WEEK_VIEW}
				label={formatMessage({id: 'overview_time.week'})}
				data-cy={'week'}
			>
				<FormattedMessage id="overview_time.week" />
			</SegmentedControl.Item>
		</SegmentedControl>
	);
};

// Parent supplying Relay info is still JS so keeping those props as implicit any. Comment indicates where the type would be found in a TS generated file.
type TimesheetsBottomSectionProps = {
	timeRegistrations; // TimesheetsPage_viewer['timeRegistrations']['edges']
	currentViewingDate: Moment;
	setCurrentViewingDate: (date: Moment) => void;
	timePeriod: TimePeriod;
	setTimePeriod: (period: TimePeriod) => void;
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	lastWorkingDayInWeek; // unknown type
	nonViewerPersonSelected: boolean;
	lockedDate?: Moment;
	customFieldDefinitions; // TimesheetsPage_viewer['company']['customFieldDefinitions]['edges'][0]['node'];
	overBudgetProgramsByCompanyProjectId; // Custom type of {companyProjectId, program}
	timeRegistrationTargetSuggestionsData: TimeRegistrationTargetSuggstionsData;
	paginationRef;
	viewer: ViewerType & TimesheetsFilters_viewer;
	timeRegSideModalOpen: boolean;
	isCopyDisabled: boolean;
	selectedPersonId: string;
	filters: DefaultFiltersType;
	setFilters: (filters: DefaultFiltersType) => void;
	searchActive: boolean;
	taskTypeSearchQuery: TaskTypeSearchQuery;
	showAllTimeTasks: boolean;
	setShowAllTimeTasks: (filters: boolean) => void;
	refetch: () => void;
};

export const TimesheetsBottomSection = ({
	timeRegistrations,
	currentViewingDate,
	setCurrentViewingDate,
	timePeriod,
	setTimePeriod,
	selectedPerson,
	nonViewerPersonSelected,
	lastWorkingDayInWeek,
	lockedDate,
	customFieldDefinitions,
	overBudgetProgramsByCompanyProjectId,
	timeRegistrationTargetSuggestionsData,
	paginationRef,
	viewer,
	timeRegSideModalOpen,
	isCopyDisabled,
	selectedPersonId,
	filters,
	setFilters,
	searchActive,
	taskTypeSearchQuery,
	showAllTimeTasks,
	setShowAllTimeTasks,
	refetch,
}: TimesheetsBottomSectionProps): React.ReactElement => {
	const {formatMessage} = useIntl();
	const isWeekView = timePeriod === TIME_PERIOD.WEEK_VIEW;

	const defaultTab = useMemo(() => {
		const calendarFetchEnabled =
			viewer.calendarIntegrations?.googleCalendar?.calendarFetchEnabled ||
			viewer.calendarIntegrations?.outlookCalendar?.calendarFetchEnabled ||
			false;
		return calendarFetchEnabled ? TimesheetTabName.Timesheet : TimesheetTabName.FindTasks;
	}, [
		viewer.calendarIntegrations?.googleCalendar?.calendarFetchEnabled,
		viewer.calendarIntegrations?.outlookCalendar?.calendarFetchEnabled,
	]);

	const [selectedTab, setSelectedTab] = useStoredState<TimesheetTabName>(LOCALSTORAGE_TIMESHEETS_SELECTED_TAB, value => {
		return isTimesheetTab(value) ? value : defaultTab;
	});

	// If defaultTab changes, switch to that tab.
	const didMount = React.useRef(false);
	useEffect(() => {
		if (didMount.current) {
			setSelectedTab(defaultTab);
		} else {
			didMount.current = true;
		}
	}, [defaultTab]);

	const sort: TimesheetColumnSort = usePersistentTimesheetColumnSort();

	const [showSuggestions, setShowSuggestions] = React.useState(false);
	const [savedFilters, setSavedFilters] = useState<JSX.Element | null>(null);

	const {eyeOptions, setEyeOptions} = useTimesheetColumnEyeOptions();

	const {allowOutsideTaskDates} = useTimeRegistrationSettings();
	const usingTimeApproval = isUsingTimeApproval(viewer.company.useTimeApproval);
	const usingInternalTimeApproval = isUsingInternalTimeApproval(viewer.company.useInternalTimeApproval);

	const validTimeRegistrationSuggestions = useMemo(
		() => getValidSuggestions(timeRegistrationTargetSuggestionsData, selectedPerson.id),
		[timeRegistrationTargetSuggestionsData, selectedPerson.id]
	);
	const hasSuggestions: boolean =
		!nonViewerPersonSelected && !!validTimeRegistrationSuggestions && validTimeRegistrationSuggestions.length > 0;

	const handleTabChange = (value: string) => {
		if (isTimesheetTab(value)) {
			trackEvent('Timesheet Table', 'Tab Changed', {prevTab: selectedTab, newTab: value});
			setSelectedTab(value);
		}
	};

	const goToTimesheetsTab = () => {
		trackEvent('Timesheet Table Totals', 'Clicked');
		handleTabChange(TimesheetTabName.Timesheet);
	};

	const setAndTrackShowSuggestions = useCallback(
		(showSuggestions: boolean) => {
			if (showSuggestions) {
				const newSuggestions =
					timeRegistrationTargetSuggestionsData?.timeRegistrationTargetSuggestions?.sortedSuggestions;
				const newSuggestionsAvailable = newSuggestions && newSuggestions.length > 0;
				trackEvent('Time Registration Suggestions', 'Shown', {version: newSuggestionsAvailable ? 'New' : 'Old'});
				trackTimeRegistrationSuggestionsShown({
					companyId: viewer.company.id,
					personId: selectedPerson.id,
					modelVersion: timeRegistrationTargetSuggestionsData?.timeRegistrationTargetSuggestions?.modelVersion,
				});
			}
			setShowSuggestions(showSuggestions);
		},
		[timeRegistrationTargetSuggestionsData]
	);

	useSubscription(EVENT_ID.SHOW_TIME_REG_SUGGESTIONS, () => {
		trackEvent('Show Suggestions Link', 'Clicked', {availableSuggestions: validTimeRegistrationSuggestions?.length});
		setAndTrackShowSuggestions(true);
	});

	const onTimesheetPersonChange = useCallback(personId => {
		handleTabChange(TimesheetTabName.Timesheet);
		setFilters({task: {person: [personId]}});
	}, []);

	useSubscription(EVENT_ID.TIMESHEET_PERSON_CHANGED, onTimesheetPersonChange);

	const usingCurrentUserAssigneeFilter = useMemo(() => {
		return !nonViewerPersonSelected && JSON.stringify(filters) === JSON.stringify({task: {person: [selectedPerson.id]}});
	}, [filters?.task]);

	const getTimesheetsApprovalStatus = (timeRegs): ApprovalStatus => {
		const projectTimeRegs = timeRegs.filter(tReg => !tReg.node.idleTime);
		let timeRegStatus: ApprovalStatus =
			projectTimeRegs.length > 0 ? APPROVAL_STATUS.APPROVED : APPROVAL_STATUS.NOT_SUBMITTED;
		for (const tReg of projectTimeRegs) {
			if (!tReg.node || (tReg.node.approvalStatus === APPROVAL_STATUS.NOT_SUBMITTED && !tReg.node.idleTime)) {
				return APPROVAL_STATUS.NOT_SUBMITTED;
			} else if (tReg.node.approvalStatus === APPROVAL_STATUS.SUBMITTED) {
				timeRegStatus = APPROVAL_STATUS.SUBMITTED;
			}
		}

		return timeRegStatus;
	};

	const weekStart = useMemo(() => currentViewingDate.clone().startOf('week'), [currentViewingDate]);
	const weekEnd = useMemo(() => currentViewingDate.clone().endOf('week'), [currentViewingDate]);

	const holidays = useMemo(() => {
		return isWeekView
			? Array(7)
					.fill(0)
					.map((_, i) => weekStart.clone().add(i, 'day'))
					.map(day => getHolidaysOfTheDay(selectedPerson, day))
			: getHolidaysOfTheDay(selectedPerson, currentViewingDate);
	}, [isWeekView, selectedPerson, currentViewingDate]);

	const isFullyLocked = useMemo(() => {
		if (lockedDate) {
			return isWeekView ? lockedDate.isSameOrAfter(weekEnd, 'd') : lockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, currentViewingDate]);

	const isWorkdaysLocked = useMemo(() => {
		if (lockedDate && lastWorkingDayInWeek) {
			return lockedDate.isSameOrAfter(lastWorkingDayInWeek, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, lastWorkingDayInWeek]);

	const timeRegsForPeriod = useMemo(() => {
		return timeRegistrations.filter(tReg => {
			const tDate = Util.CreateNonUtcMomentDate(tReg.node.year, tReg.node.month, tReg.node.day);
			if (tReg.node.person.id !== selectedPerson.id) {
				return false; // Only show time registrations for the selected person
			}
			if (isWeekView) {
				return tDate?.isSameOrAfter(weekStart, 'days') && tDate.isSameOrBefore(weekEnd, 'days');
			} else {
				return currentViewingDate.isSame(tDate, 'date');
			}
		});
	}, [timeRegistrations, weekStart, weekEnd, isWeekView]);

	// IF we loaded on TimesheetTabName.Timesheet but there are no time entries for the selected date range, switch to TimesheetTabName.FindTasks
	useEffect(() => {
		if (selectedTab === TimesheetTabName.Timesheet && timeRegsForPeriod.length === 0) {
			setSelectedTab(TimesheetTabName.FindTasks);
		}
	}, []);

	const periodTimeApprovalStatus = usingTimeApproval ? getTimesheetsApprovalStatus(timeRegsForPeriod) : null;

	// Hide suggestions if the dates are changed
	useEffect(() => {
		setAndTrackShowSuggestions(false);
	}, [currentViewingDate, isWeekView]);
	// Hide suggestions if showing someone elses time regs
	useEffect(() => {
		if (nonViewerPersonSelected) {
			setAndTrackShowSuggestions(false);
		}
	}, [nonViewerPersonSelected]);

	const toggleSuggestions = () => {
		trackEvent(showSuggestions ? 'Hide Suggested Entries Button' : 'Reveal Suggested Entries Button', 'Clicked', {
			availableSuggestions: validTimeRegistrationSuggestions?.length,
		});
		setAndTrackShowSuggestions(!showSuggestions);
	};

	const hasSuggestionWithoutTimeReg = useMemo(
		() => suggestionsWithoutTimeRegs(timeRegsForPeriod, validTimeRegistrationSuggestions),
		[validTimeRegistrationSuggestions, timeRegsForPeriod]
	);

	let suggestionTooltip: string | null = null;
	if (!hasSuggestions) {
		suggestionTooltip = formatMessage({id: 'timesheets.tooltip.no_suggestions'});
	} else if (!hasSuggestionWithoutTimeReg) {
		suggestionTooltip = formatMessage({id: 'timesheets.tooltip.no_relevant_suggestions'});
	}

	const onCopyFromLastWeekClicked = () => {
		const copyFullWeek = timePeriod === TIME_PERIOD.WEEK_VIEW;
		if (copyFullWeek) {
			trackEvent('Copy Previous Week Button', 'Clicked');
		} else {
			trackEvent('Copy Previous Day Button', 'Clicked');
		}
		showModal({
			type: MODAL_TYPE.COPY_FROM_LAST_WEEK,
			personId: selectedPersonId,
			selectedDate: currentViewingDate.toDate(),
			copyFullWeek: timePeriod === TIME_PERIOD.WEEK_VIEW,
		});
	};

	const selectDate = useCallback(
		(day: Moment) => {
			setTimePeriod(TIME_PERIOD.DAY_VIEW);
			setCurrentViewingDate(day);
		},
		[setTimePeriod, setCurrentViewingDate]
	);

	return (
		<BottomSectionWrapper data-userpilot="timesheets-task-list" timeRegSideModalOpen={timeRegSideModalOpen}>
			<TabSectionWrapper>
				<FlexRow justifyContent={'space-between'}>
					<FlexRow gap={'xl'} className={'header-bar no-padding header-bar-left'}>
						<Tabs value={selectedTab} onValueChange={handleTabChange}>
							<Tabs.List>
								<TimesheetTab timeRegsForPeriod={timeRegsForPeriod} />
								<InternalTimeTab viewer={viewer} />
								<FindTasksTab taskTypeSearchQuery={taskTypeSearchQuery} />
							</Tabs.List>
						</Tabs>
						<PeriodToggle timePeriod={timePeriod} setTimePeriod={setTimePeriod} />
					</FlexRow>
					<FlexRow justifyContent={'flex-end'} gap={'xs'} className={'header-bar no-padding header-bar-right'}>
						{(selectedTab === TimesheetTabName.FindTasks || selectedTab === TimesheetTabName.Timesheet) && (
							<TheEye
								expandLeft={true}
								key={`the-eye`}
								options={eyeOptions}
								onSelect={(_, __, ___, newOptions) => setEyeOptions(newOptions)}
								useDesignSystemButton={true}
								emphasis={EMPHASIS.MONO}
							/>
						)}
						{selectedTab === TimesheetTabName.FindTasks && (
							<TimesheetsFilters
								filters={filters}
								setFilters={setFilters}
								setSavedFilters={setSavedFilters}
								viewer={viewer}
								emphasis={EMPHASIS.MONO}
							/>
						)}
						{selectedTab === TimesheetTabName.Timesheet && (
							<Button
								id={'copy-from-last-week'}
								data-cy={'copy-from-last-week-button'}
								disabled={isCopyDisabled}
								onPress={onCopyFromLastWeekClicked}
								emphasis={EMPHASIS.MONO}
							>
								{timePeriod === TIME_PERIOD.WEEK_VIEW
									? formatMessage({id: 'timesheets.copy_previous_week'})
									: formatMessage({id: 'timesheets.copy_previous_day'})}
							</Button>
						)}
						{selectedTab === TimesheetTabName.Timesheet && (
							<ForecastTooltip content={suggestionTooltip}>
								<Button
									data-cy={'toggle-suggestions-button'}
									disabled={!hasSuggestionWithoutTimeReg || !hasSuggestions}
									icon={showSuggestions ? 'sparkleHide' : 'sparkle'}
									onPress={toggleSuggestions}
									emphasis={EMPHASIS.MONO}
								>
									{showSuggestions && hasSuggestionWithoutTimeReg
										? formatMessage({id: 'timesheets.suggestions.hide_suggestions'})
										: formatMessage({id: 'timesheets.suggestions.show_suggestions'})}
								</Button>
							</ForecastTooltip>
						)}
					</FlexRow>
				</FlexRow>
			</TabSectionWrapper>
			<InfoSectionWrapper justifyContent={'space-between'}>
				<FlexRow>
					<DateInfoSection
						timePeriod={timePeriod}
						selectedTab={selectedTab}
						currentViewingDate={currentViewingDate}
						weekStart={weekStart}
						weekEnd={weekEnd}
						showAllTimeTasks={showAllTimeTasks}
						holidays={holidays}
					/>
					{allowOutsideTaskDates && selectedTab === TimesheetTabName.FindTasks ? (
						<Button
							icon={'closeS'}
							onPress={() => setShowAllTimeTasks(!showAllTimeTasks)}
							emphasis={EMPHASIS.LOW}
						/>
					) : null}
					{periodTimeApprovalStatus &&
					isWorkdaysLocked &&
					periodTimeApprovalStatus !== APPROVAL_STATUS.NOT_SUBMITTED ? (
						periodTimeApprovalStatus === APPROVAL_STATUS.APPROVED ? (
							<HeaderLabel text={'Approved'} color={HeaderLabel.INDICATOR_COLOR.GREEN} />
						) : (
							<HeaderLabel text={'Submitted'} color={HeaderLabel.INDICATOR_COLOR.BLUE} />
						)
					) : null}
				</FlexRow>
				{selectedTab === TimesheetTabName.FindTasks && savedFilters}
			</InfoSectionWrapper>
			<TableWrapper>
				{selectedTab === TimesheetTabName.FindTasks ? (
					<ForecastQueryRenderer
						key="query-render-TimesheetThisWeeksWorkTableWrapper"
						query={TimesheetThisWeeksWorkTableWrapperQuery}
						customLoader={() => <InlineLoader />}
						fetchPolicy={'network-only'}
						variables={{
							pageSize: TIMESHEET_THIS_WEEKS_WORK_PAGE_SIZE,
							taskTypeSearchQuery: taskTypeSearchQuery,
							sortValue: serialiseSort(sort),
							timePeriod: timePeriod,
							currentViewingDate: currentViewingDate.format('YYYY-MM-DD'),
							personId: selectedPerson.id,
						}}
						render={(relayProps: RelayProps<TimesheetThisWeeksWorkTableWrapper_viewer>) => {
							return (
								<TimesheetThisWeeksWorkTableWrapper
									{...relayProps}
									paginationRef={paginationRef}
									selectedPerson={selectedPerson}
									timeRegistrations={timeRegistrations}
									timeRegsForPeriod={timeRegsForPeriod}
									currentViewingDate={currentViewingDate}
									timePeriod={timePeriod}
									filterActive={!!filters?.task}
									searchActive={searchActive}
									usingCurrentUserAssigneeFilter={usingCurrentUserAssigneeFilter}
									sort={sort}
									selectDate={selectDate}
									holidays={holidays}
									lockedDate={lockedDate}
									isFullyLocked={isFullyLocked}
									usingTimeApproval={usingTimeApproval}
									usingInternalTimeApproval={usingInternalTimeApproval}
									periodTimeApprovalStatus={periodTimeApprovalStatus}
									overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
									timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
									goToTimesheetsTab={goToTimesheetsTab}
									refetchEntries={refetch}
									customFieldDefinitions={customFieldDefinitions}
								/>
							);
						}}
					/>
				) : selectedTab === TimesheetTabName.InternalTime ? (
					<ForecastQueryRenderer
						key="query-render-TimesheetInternalTimeTableWrapper"
						query={TimesheetInternalTimeTableWrapperQuery}
						customLoader={() => <InlineLoader />}
						fetchPolicy={'network-only'}
						render={(relayProps: RelayProps<TimesheetInternalTimeTableWrapper_viewer>) => {
							return (
								<TimesheetInternalTimeTableWrapper
									{...relayProps}
									selectedPerson={selectedPerson}
									timeRegistrations={timeRegistrations}
									timeRegsForPeriod={timeRegsForPeriod}
									currentViewingDate={currentViewingDate}
									timePeriod={timePeriod}
									sort={sort}
									selectDate={selectDate}
									holidays={holidays}
									lockedDate={lockedDate}
									isFullyLocked={isFullyLocked}
									usingTimeApproval={usingTimeApproval}
									usingInternalTimeApproval={usingInternalTimeApproval}
									periodTimeApprovalStatus={periodTimeApprovalStatus}
									overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
									timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
									goToTimesheetsTab={goToTimesheetsTab}
									showSuggestions={showSuggestions}
									hasSuggestions={hasSuggestions}
									refetchEntries={refetch}
								/>
							);
						}}
					/>
				) : (
					<ForecastQueryRenderer
						key="query-render-TimesheetTimeTableWrapper"
						query={TimesheetTimeTableWrapperQuery}
						customLoader={() => <InlineLoader />}
						render={(relayProps: RelayProps<TimesheetTimeTableWrapper_viewer>) => {
							return (
								<TimesheetTimeTableWrapper
									{...relayProps}
									selectedPerson={selectedPerson}
									timeRegistrations={timeRegistrations}
									timeRegsForPeriod={timeRegsForPeriod}
									currentViewingDate={currentViewingDate}
									timePeriod={timePeriod}
									sort={sort}
									selectDate={selectDate}
									holidays={holidays}
									lockedDate={lockedDate}
									isFullyLocked={isFullyLocked}
									usingTimeApproval={usingTimeApproval}
									usingInternalTimeApproval={usingInternalTimeApproval}
									periodTimeApprovalStatus={periodTimeApprovalStatus}
									overBudgetProgramsByCompanyProjectId={overBudgetProgramsByCompanyProjectId}
									// The full set of suggestions and the valid suggestions are passed along separately because the tracking requires the full set, but the table itself only requires the valid
									timeRegistrationTargetSuggestionsData={timeRegistrationTargetSuggestionsData}
									validTimeRegistrationSuggestions={validTimeRegistrationSuggestions}
									goToTimesheetsTab={goToTimesheetsTab}
									showSuggestions={showSuggestions}
									hasSuggestions={hasSuggestions}
									refetchEntries={refetch}
									customFieldDefinitions={customFieldDefinitions}
								/>
							);
						}}
					/>
				)}
			</TableWrapper>
		</BottomSectionWrapper>
	);
};
